import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import SkeletonPage, { SkeletonCard, SkeletonTable } from "@/components/KovaSkeleton";
import {
  ArrowLeft, Landmark, User, Phone, MapPin, CheckCircle2, Circle, XCircle, AlertTriangle,
  FileText, Calendar, ArrowRight, Ban, ShieldAlert, Receipt,
} from "lucide-react";
import { toast } from "sonner";

const HAPPY_PATH = [
  "new_enquiry", "documents_pending", "documents_verified", "application_submitted",
  "under_scrutiny", "approved", "permit_generated", "permit_received", "ready_for_sale",
  "sale_completed", "subsidy_completed",
];

const STATUS_LABEL = {
  new_enquiry: "New Enquiry", documents_pending: "Documents Pending",
  documents_verified: "Documents Verified", application_submitted: "Application Submitted",
  under_scrutiny: "Under Scrutiny", approved: "Approved", rejected: "Rejected",
  permit_generated: "Permit Generated", permit_received: "Permit Received",
  ready_for_sale: "Ready For Sale", sale_completed: "Sale Completed",
  subsidy_completed: "Subsidy Completed", expired: "Expired", cancelled: "Cancelled",
};

export default function SubsidyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["admin", "co_admin", "subsidy_desk_manager", "subsidy_desk_executive"].includes(user?.role);
  const isAdmin = ["admin", "co_admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [txnOpen, setTxnOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState("");
  const [txnForm, setTxnForm] = useState({ note: "", permit_number: "", permit_date: "", permit_expiry: "", approved_machine_category: "", approved_amount: "", approved_percentage: "" });

  const [accessDenied, setAccessDenied] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/subsidies/${id}`);
      setData(r.data);
      setAccessDenied(false);
    } catch (err) {
      if (err?.response?.status === 403) setAccessDenied(true);
      else toast.error("Could not load subsidy case");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const canTransition = (target) => {
    if (isAdmin) return true;
    const allowed = ALLOWED[data?.status] || [];
    return allowed.includes(target);
  };

  const openTxn = (target) => {
    setPendingStatus(target);
    setTxnForm({ note: "", permit_number: data.permit_number || "", permit_date: data.permit_date || "", permit_expiry: data.permit_expiry || "", approved_machine_category: data.approved_machine_category || data.machine_category || "", approved_amount: data.approved_amount || data.expected_amount || "", approved_percentage: data.approved_percentage || data.expected_percentage || "" });
    setTxnOpen(true);
  };

  const submitTxn = async (force = false) => {
    try {
      const payload = { to_status: pendingStatus, note: txnForm.note };
      if (pendingStatus === "permit_generated") {
        payload.permit_number = txnForm.permit_number;
        payload.permit_date = txnForm.permit_date;
        payload.permit_expiry = txnForm.permit_expiry;
        payload.approved_machine_category = txnForm.approved_machine_category;
        payload.approved_amount = parseFloat(txnForm.approved_amount) || 0;
        payload.approved_percentage = parseFloat(txnForm.approved_percentage) || 0;
      }
      await api.post(`/subsidies/${id}/transition${force ? "?force=true" : ""}`, payload);
      toast.success(`Moved to ${STATUS_LABEL[pendingStatus]}`);
      setTxnOpen(false); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to move");
    }
  };

  const convertToSale = () => {
    navigate(`/sales?customer=${data.customer_id}&subsidy=${data.id}`);
    toast.info("Choose the exact machine model, quantity and price to complete the sale.");
  };

  if (accessDenied) {
    return (
      <div className="p-8 max-w-xl mx-auto" data-testid="subsidy-detail-access-denied">
        <div className="rounded-xl border border-[#3A4640] bg-[#121815] p-8 text-center">
          <AlertTriangle size={28} className="mx-auto text-[#FFB46B] mb-3" />
          <div className="text-base font-heading font-semibold text-[#F8F9FA]">Access restricted</div>
          <p className="text-sm text-[#8A8F98] mt-2">
            Your role doesn't have permission to view this subsidy case.
          </p>
        </div>
      </div>
    );
  }
  if (!data) return <SkeletonPage title kpi table rows={4} cols={4} />;

  const stepIndex = HAPPY_PATH.indexOf(data.status);
  const isRejected = data.status === "rejected";
  const isExpired = data.status === "expired";
  const isCancelled = data.status === "cancelled";
  const daysToExpiry = data.permit_expiry ? Math.round((new Date(data.permit_expiry) - new Date()) / 86400000) : null;

  return (
    <div className="p-8 lg:p-10 max-w-[1400px]">
      <Link to="/subsidies" className="inline-flex items-center gap-2 text-sm text-[#8A8F98] hover:text-[#F8F9FA] mb-4" data-testid="back-to-subsidies">
        <ArrowLeft size={16} /> Back to Subsidy Desk
      </Link>

      {/* Header */}
      <div className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm p-6 lg:p-8 mb-6">
        <div className="flex flex-wrap items-start gap-4">
          <div className="w-14 h-14 rounded-md bg-[#39FF14] flex items-center justify-center shrink-0">
            <Landmark size={26} className="text-white" />
          </div>
          <div className="flex-1 min-w-[260px]">
            <div className="font-mono text-xs text-[#8A8F98]" data-testid="detail-case-no">{data.case_no}</div>
            <h1 className="font-heading text-3xl font-extrabold text-[#F8F9FA] mt-0.5">{data.scheme || "Untitled scheme"}</h1>
            <div className="text-sm text-[#8A8F98] mt-1">{data.department}{data.financial_year ? " · FY " + data.financial_year : ""}</div>
            {daysToExpiry !== null && ["permit_generated", "permit_received", "ready_for_sale"].includes(data.status) && daysToExpiry <= 15 && (
              <div className={`inline-flex items-center gap-1.5 mt-2 text-xs px-2 py-1 rounded ${daysToExpiry < 0 ? "bg-red-500/15 text-red-300" : "bg-yellow-500/10 text-yellow-200 border border-yellow-500/30"}`} data-testid="permit-expiry-warning">
                <AlertTriangle size={12} />
                {daysToExpiry < 0 ? `Permit expired ${Math.abs(daysToExpiry)}d ago` : `Permit expires in ${daysToExpiry} day${daysToExpiry === 1 ? "" : "s"}`}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {data.status === "ready_for_sale" && canEdit && (
              <Button onClick={convertToSale} data-testid="convert-to-sale-btn" className="bg-yellow-500 hover:bg-yellow-600 text-[#0B0F0C]">
                <Receipt size={14} className="mr-2" /> Convert to Sale
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Workflow stepper */}
      <section className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm p-5 mb-6" data-testid="workflow-stepper">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-[#F8F9FA]">Workflow</h3>
          {isAdmin && data.status !== "subsidy_completed" && (
            <span className="text-[11px] text-[#8A8F98]">Admins can force-move to any status.</span>
          )}
        </div>

        <ol className="flex flex-wrap gap-2 mb-6">
          {HAPPY_PATH.map((s, i) => {
            const done = stepIndex >= i && !isRejected;
            const current = data.status === s;
            return (
              <li key={s} className="flex items-center gap-2">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition-colors ${current ? "bg-[#39FF14] text-[#0B0F0C] border-[#0f4c26] shadow" : done ? "bg-green-50 text-green-900 border-[#39FF14]/25" : "bg-[#0B0F0C] text-[#8A8F98] border-[#3A4640]"}`} data-testid={`step-${s}`}>
                  {done && !current ? <CheckCircle2 size={12} /> : current ? <Circle size={10} className="fill-white" /> : <Circle size={12} />}
                  <span className="font-heading font-semibold">{STATUS_LABEL[s]}</span>
                </div>
                {i < HAPPY_PATH.length - 1 && <ArrowRight size={12} className="text-[#8A8F98]" />}
              </li>
            );
          })}
        </ol>

        {(isRejected || isExpired || isCancelled) && (
          <div className={`p-3 rounded border mb-4 flex items-center gap-2 text-sm ${isRejected ? "bg-red-500/10 border-red-500/25 text-red-900" : "bg-[#121815] border-[#3A4640] text-[#F8F9FA]"}`} data-testid="terminal-status">
            {isRejected ? <XCircle size={16} /> : isCancelled ? <Ban size={16} /> : <ShieldAlert size={16} />}
            <div><b>{STATUS_LABEL[data.status]}</b> — this case is terminated.</div>
          </div>
        )}

        {canEdit && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading mr-1">Move to →</span>
            {ALL_TARGETS.filter((t) => t !== data.status).map((t) => {
              const ok = canTransition(t);
              if (!isAdmin && !ok) return null;
              return (
                <button
                  key={t}
                  onClick={() => openTxn(t)}
                  data-testid={`transition-${t}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${ok ? "border-[#39FF14]/40 text-[#39FF14] hover:bg-green-50" : "border-red-500/25 text-red-600 hover:bg-red-500/10"}`}
                  title={ok ? "Normal transition" : "Admin override — will be recorded as forced"}
                >
                  {STATUS_LABEL[t]}{!ok && " (force)"}
                </button>
              );
            })}
          </div>
        )}
      </section>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Customer */}
        <Card title="Customer" icon={User}>
          <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">TZAT</div>
          <div className="font-mono text-sm">{data.customer?.tzat_no}</div>
          <Link to={`/customers/${data.customer_id}`} className="font-heading font-bold text-[#F8F9FA] hover:text-[#39FF14]" data-testid="detail-customer-link">
            {data.customer?.name || data.customer?.organization_name || data.customer?.department_name || "—"}
          </Link>
          <div className="text-xs text-[#8A8F98] capitalize mt-0.5">{(data.customer?.customer_type || "").replace(/_/g, " ")}</div>
          <div className="mt-3 space-y-1 text-xs text-[#F8F9FA]">
            {data.customer?.phone && <div className="flex items-center gap-1"><Phone size={11} /> {data.customer.phone}</div>}
            {data.customer?.alternate_phone && <div className="flex items-center gap-1 text-[#8A8F98]"><Phone size={11} /> {data.customer.alternate_phone}</div>}
            {(data.customer?.village || data.customer?.district) && (
              <div className="flex items-start gap-1"><MapPin size={11} className="mt-0.5" /> <span>{[data.customer?.village, data.customer?.gram_panchayat, data.customer?.block, data.customer?.district].filter(Boolean).join(", ")}</span></div>
            )}
          </div>
        </Card>

        {/* Application */}
        <Card title="Application" icon={FileText}>
          <KV label="Application number" value={data.application_number || "—"} mono />
          <KV label="Application date" value={data.application_date ? formatDate(data.application_date) : "—"} />
          <KV label="Machine category (requested)" value={data.machine_category || "—"} />
          <KV label="Expected amount" value={formatCurrency(data.expected_amount || 0)} />
          <KV label="Expected %" value={data.expected_percentage ? `${data.expected_percentage}%` : "—"} />
          {data.remarks && <KV label="Remarks" value={data.remarks} />}
        </Card>

        {/* Permit */}
        <Card title="Permit" icon={Landmark}>
          {data.permit_number ? (
            <>
              <KV label="Permit number" value={data.permit_number} mono />
              <KV label="Permit date" value={data.permit_date ? formatDate(data.permit_date) : "—"} />
              <KV label="Permit expiry" value={data.permit_expiry ? formatDate(data.permit_expiry) : "—"} />
              <KV label="Approved category" value={data.approved_machine_category || "—"} />
              <KV label="Approved amount" value={formatCurrency(data.approved_amount || 0)} />
              <KV label="Approved %" value={data.approved_percentage ? `${data.approved_percentage}%` : "—"} />
            </>
          ) : (
            <div className="text-sm text-[#8A8F98]">Permit details will appear once the case reaches <b>Permit Generated</b>.</div>
          )}
        </Card>

        {/* History */}
        <div className="lg:col-span-3 bg-[#121815] border border-[#3A4640] rounded-md shadow-sm p-5">
          <h3 className="font-heading font-bold text-[#F8F9FA] mb-4 flex items-center gap-2"><Calendar size={16} className="text-[#39FF14]" /> Case history</h3>
          {(data.history || []).length === 0 ? (
            <div className="text-sm text-[#8A8F98]">No transitions yet.</div>
          ) : (
            <ol className="relative border-l-2 border-[#3A4640] ml-3 space-y-3" data-testid="subsidy-history">
              {[...(data.history || [])].reverse().map((h, idx) => (
                <li key={idx} className="pl-5 relative">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full flex items-center justify-center bg-[#39FF14] shadow">
                    <CheckCircle2 size={9} className="text-white" />
                  </div>
                  <div className="text-sm text-[#F8F9FA] font-medium">{STATUS_LABEL[h.from] || "—"} → {STATUS_LABEL[h.to] || h.to}</div>
                  <div className="text-xs text-[#8A8F98]">{formatDate(h.at)} · {h.user_name}{h.forced && <span className="ml-2 text-red-600 font-heading uppercase tracking-wider">Forced</span>}</div>
                  {h.note && <div className="text-xs text-[#F8F9FA] mt-1 italic">&ldquo;{h.note}&rdquo;</div>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {/* Transition dialog */}
      <Dialog open={txnOpen} onOpenChange={setTxnOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Move to {STATUS_LABEL[pendingStatus]}</DialogTitle>
            <DialogDescription>Every status change is recorded with your name and the current time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {pendingStatus === "permit_generated" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Permit number</Label><Input value={txnForm.permit_number} onChange={(e) => setTxnForm({ ...txnForm, permit_number: e.target.value })} data-testid="permit-number-input" /></div>
                  <div><Label>Permit date</Label><Input type="date" value={txnForm.permit_date} onChange={(e) => setTxnForm({ ...txnForm, permit_date: e.target.value })} /></div>
                  <div><Label>Permit expiry</Label><Input type="date" value={txnForm.permit_expiry} onChange={(e) => setTxnForm({ ...txnForm, permit_expiry: e.target.value })} data-testid="permit-expiry-input" /></div>
                  <div><Label>Approved category</Label><Input value={txnForm.approved_machine_category} onChange={(e) => setTxnForm({ ...txnForm, approved_machine_category: e.target.value })} /></div>
                  <div><Label>Approved amount ({"\u20B9"})</Label><Input type="number" value={txnForm.approved_amount} onChange={(e) => setTxnForm({ ...txnForm, approved_amount: e.target.value })} /></div>
                  <div><Label>Approved %</Label><Input type="number" value={txnForm.approved_percentage} onChange={(e) => setTxnForm({ ...txnForm, approved_percentage: e.target.value })} /></div>
                </div>
              </>
            )}
            <div>
              <Label>Note (optional)</Label>
              <Textarea rows={2} value={txnForm.note} onChange={(e) => setTxnForm({ ...txnForm, note: e.target.value })} placeholder="Add context for the audit trail" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setTxnOpen(false)}>Cancel</Button>
            {isAdmin && !ALLOWED[data.status]?.includes(pendingStatus) && (
              <Button onClick={() => submitTxn(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="force-txn-btn">Force move (admin)</Button>
            )}
            <Button onClick={() => submitTxn(false)} className="bg-[#39FF14] hover:bg-[#2ECC71]" data-testid="confirm-txn-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Same adjacency map as backend — kept small on purpose.
const ALLOWED = {
  new_enquiry: ["documents_pending", "cancelled"],
  documents_pending: ["documents_verified", "cancelled"],
  documents_verified: ["application_submitted", "cancelled"],
  application_submitted: ["under_scrutiny", "cancelled"],
  under_scrutiny: ["approved", "rejected", "cancelled"],
  approved: ["permit_generated", "cancelled"],
  rejected: ["cancelled"],
  permit_generated: ["permit_received", "expired", "cancelled"],
  permit_received: ["ready_for_sale", "expired", "cancelled"],
  ready_for_sale: ["sale_completed", "expired", "cancelled"],
  sale_completed: ["subsidy_completed"],
  subsidy_completed: [],
  expired: [],
  cancelled: [],
};
const ALL_TARGETS = [
  "documents_pending", "documents_verified", "application_submitted", "under_scrutiny",
  "approved", "rejected", "permit_generated", "permit_received", "ready_for_sale",
  "sale_completed", "subsidy_completed", "expired", "cancelled",
];

function Card({ title, icon: Icon, children }) {
  return (
    <section className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm p-5">
      <h3 className="font-heading font-bold text-[#F8F9FA] mb-3 flex items-center gap-2"><Icon size={16} className="text-[#39FF14]" /> {title}</h3>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function KV({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">{label}</div>
      <div className={`text-sm text-[#F8F9FA] ${mono ? "font-mono" : "font-medium"}`}>{value}</div>
    </div>
  );
}
